import React, { useState, useRef } from "react";
import { useTrans } from "../../hooks";
import PropTypes from "prop-types";
import {
  withValidation,
  FormField,
  NewCheckbox
} from "../../shared-components";
import { Button } from "@wattpad/web-ui-library";
import ReactTooltip from "react-tooltip";
import { Icon } from "../../components";
import BirthdayFields from "../authentication/BirthdayFields";

const ValidatedFormField = withValidation(FormField);

const RequestAccountDetails = ({ errorMsg, model, preservedVal = {} }) => {
  const { trans } = useTrans();

  const { token, type } = preservedVal;

  // eslint-disable-next-line no-unused-vars
  const [usernameValue, setUsernameValue] = useState();
  // eslint-disable-next-line no-unused-vars
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [isBirthdayValid, setIsBirthdayValid] = useState(false);
  const [isPolicyConsentValid, setIsPolicyConsentValid] = useState(false);
  const [isMarketingConsentValid, setIsMarketingConsentValid] = useState(false);
  const [isInvalidated, setIsInvalidated] = useState(false);
  const [policy, setIsPolicy] = useState("");

  const usernameRef = useRef();
  const birthdayInput = useRef();
  const pronounInput = useRef();

  const policy_agreement = trans("Yes, I have read and agree to Wattpad's <a href='/terms'>Terms of Service</a> and <a href='/privacy'>Privacy Policy</a>."); // prettier-ignore
  const marketing_agreement_email = trans(
    "Yes, I'd like to receive marketing emails from Wattpad. (optional)"
  );
  const marketing_agreement_email_subtext = trans(
    "Get story recommendations, announcements, offers, and more via email. Unsubscribe anytime."
  );

  const validateSignupForm = (isValid, key) => {
    if (key.toLowerCase().includes("username")) {
      setIsUsernameValid(isValid);
    } else if (key.toLowerCase().includes("email")) {
      setIsEmailValid(isValid);
    } else if (key.toLowerCase().includes("birthday")) {
      setIsBirthdayValid(isValid);
    }
  };

  const onSubmitErrorMessage =
    errorMsg === trans("Please fix any errors to continue.");

  const onBlurHandler = e => {
    const valid = e.target.checkValidity();
    setIsPolicy(valid);
  };

  const onChangeHandler = () => {
    setIsPolicyConsentValid(!isPolicyConsentValid);
    setIsPolicy(!isPolicyConsentValid);
  };

  const renderLabelWithHint = (label, ariaLabel, tooltip) => (
    <div className="field-label">
      <span className="field-label-text">{label}</span>
      <div aria-label={ariaLabel} data-tip={tooltip}>
        <Icon
          height="16"
          iconName="fa-info"
          color="wp-neutral-1"
          className="tooltip-icon"
        />
      </div>
    </div>
  );

  const onSubmitHandler = () => {
    birthdayInput.current.focus();
    birthdayInput.current.blur();

    usernameRef.current.focus();
    usernameRef.current.blur();

    setIsPolicy(isPolicyConsentValid);
    if (!isBirthdayValid || !isPolicyConsentValid || !isUsernameValid)
      setIsInvalidated(true);
    else setIsInvalidated(false);
  };

  return (
    <div className="container-new">
      <div className="form-wrapper">
        <div className="form-new">
          <form
            id="signupForm"
            action=""
            method="POST"
            onSubmit={() => {
              if (!isPolicyConsentValid) {
                return false;
              }
            }}
          >
            <ReactTooltip className="tool-tip" effect="solid" />
            {renderLabelWithHint(
              trans("Username"),
              "username hint",
              trans(
                "You do not have to use your real name. You can choose to use another name to protect your privacy."
              )
            )}
            <ValidatedFormField
              name="username"
              form="signup"
              inputType="text"
              title={trans("Enter username")}
              inputRef={usernameRef}
              validationModel={model}
              showLabel={true}
              onValid={key => validateSignupForm(true, key)}
              onInvalid={key => validateSignupForm(false, key)}
              onBlur={() => setUsernameValue(usernameRef.current?.value)}
            />

            {renderLabelWithHint(
              trans("Birthday"),
              "why we ask for your birthday",
              trans(
                "You need to enter the date you were born. This information will only be visible to you and Wattpad’s Support teams."
              )
            )}
            <BirthdayFields
              inputRef={birthdayInput}
              validationModel={model}
              onValid={() => validateSignupForm(true, "birthday")}
              onInvalid={() => validateSignupForm(false, "birthday")}
              errorMessageOnSubmit={
                onSubmitErrorMessage ? trans("Birthday is required.") : ""
              }
            />
            {renderLabelWithHint(
              trans("Pronouns (optional)"),
              trans("Why we ask for your pronouns"),
              trans(
                "Your pronouns are only visible to you and Wattpad's support team. Update them anytime in your profile."
              )
            )}
            <FormField
              inputType="pronouns"
              id="pronouns"
              name="pronouns"
              defaultValue=""
              aria-label="Select Pronoun"
              ref={pronounInput}
            />
            <div className="marketing-policy-agreement-container">
              <NewCheckbox
                contentId="marketing-agreement"
                checked={isMarketingConsentValid}
                onChange={() =>
                  setIsMarketingConsentValid(!isMarketingConsentValid)
                }
                color="ds-base-2-60"
                name="marketing"
                value={isMarketingConsentValid}
              />
              <div>
                <span
                  className="marketing-agreement"
                  dangerouslySetInnerHTML={{
                    __html: marketing_agreement_email
                  }}
                />
                <br />
                <span
                  className={"marketing-agreement-subtext"}
                  dangerouslySetInnerHTML={{
                    __html: marketing_agreement_email_subtext
                  }}
                />
              </div>
            </div>
            <div className="email-policy-agreement-container">
              <NewCheckbox
                contentId="policy-consent"
                required
                checked={isPolicyConsentValid}
                onChange={onChangeHandler}
                onBlur={onBlurHandler}
                color="ds-base-2-60"
                classname={
                  policy === false || onSubmitErrorMessage ? "invalid" : ""
                }
                name="policy"
                value={isPolicyConsentValid}
              />
              <div>
                <span
                  className="policy-agreement"
                  dangerouslySetInnerHTML={{
                    __html: policy_agreement
                  }}
                />
                <br />
                {(policy === false || onSubmitErrorMessage) && (
                  <span
                    aria-live="assertive"
                    className="error-msg-new"
                    role="alert"
                  >
                    {trans("Checking this box is required.")}
                  </span>
                )}
              </div>
            </div>
            {token && (
              <input
                className="token hidden"
                readOnly
                value={token}
                name="token"
              />
            )}
            {type && (
              <input
                className="account-type hidden"
                readOnly
                value={type}
                name="type"
              />
            )}
            {errorMsg &&
              !onSubmitErrorMessage && (
                <div className="alert alert-danger" role="alert">
                  {errorMsg}
                </div>
              )}
            <Button
              fullWidth={true}
              label={trans("Sign up")}
              ariaLabel={trans("Sign up")}
              className="signup-button"
              onClick={onSubmitHandler}
            />
            {errorMsg &&
              onSubmitErrorMessage && (
                <span className="error-msg-new text-align-center" role="alert">
                  {errorMsg}
                </span>
              )}
            {isInvalidated && (
              <span className="error-msg-new text-align-center" role="alert">
                {trans("Please fix any errors to continue.")}
              </span>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

RequestAccountDetails.propTypes = {
  errorMsg: PropTypes.string,
  model: PropTypes.object,
  preservedVal: PropTypes.shape({
    email: PropTypes.string,
    token: PropTypes.string,
    type: PropTypes.string,
    username: PropTypes.string
  })
};

export default RequestAccountDetails;
